import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { getPreviousPatientInstructions, updateDropShipInstructions } from '../util/apiUtils';
import RouteNumberInput from '../components/RouteNumberInput';
import TestPrepInfo from './TestPrepInfo';
import { isUKOrder } from '../util/gearOrderUtil';

/**
 * This component displays and allows editing of the Patient Instructions and Clinical Findings.
 *
 * This component is wrapped in 'forwardRef' so the parent component can initiate this component's save and reset functions.
 */
const DropShipInstructionsForm = forwardRef(
	({ gsdlNo, orderableItem, gearRequisition, gearOrder }, ref) => {
		const [previousInstructions, setPreviousInstructions] = useState([]);
		const [patientInstructions, setPatientInstructions] = useState(gearRequisition?.patientInstructions ?? '');
		const [showClinicalFindings, setShowClinicalFindings] = useState(false);
		const [clinicalFindings, setClinicalFindings] = useState(gearRequisition?.clinicalFindings ?? '');
		const [routeNumber, setRouteNumber] = useState(gearRequisition?.routeNumber ?? '');

		const orderableItemId = orderableItem?.id;
		const gearReqId = gearRequisition?.id;

		// Load a new list of previous instructions whenever the gsdlNo or orderable item id changes
		useEffect(() => {
			loadPreviousInstructions({ gsdlNo, orderableItemId });
		}, [gsdlNo, orderableItemId]);

		// Update showClinicalFindings whenever the orderable item changes
		useEffect(() => {
			if (orderableItem?.inputFields) {
				orderableItem.inputFields.forEach((field, index) => {
					// Only show Clinical Findings if the req has that input field configured
					if (field.name === 'Clinical Findings') {
						setShowClinicalFindings(true);
					}
				});
			}
		}, [orderableItem]);

		/**
		 * Send a request to the back-end to save the current values
		 */
		const saveInstructions = useCallback(() => {
			return updateDropShipInstructions({
				gearOrderId: gearOrder?.id,
				gearReqId,
				patientInstructions,
				clinicalFindings,
				routeNumber
			});
		}, [gearOrder, gearReqId, patientInstructions, clinicalFindings, routeNumber]);

		/**
		 * 'dirty' will be true when the instructions or findings do not match the initial values.
		 */
		const isDirty = useCallback(() => {
			return patientInstructions !== (gearRequisition?.patientInstructions ?? '') || clinicalFindings !== (gearRequisition?.clinicalFindings ?? '');
		}, [clinicalFindings, gearRequisition.clinicalFindings, gearRequisition.patientInstructions, patientInstructions]);

		/**
		 * This method builds the ref properties that will be available to the parent. We are exposing
		 * two functions, 'saveInstructions' and 'reset'.
		 */
		useImperativeHandle(
			ref,
			() => ({
				save: saveInstructions,
				reset() {
					setPatientInstructions(gearRequisition?.patientInstructions ?? '');
					setClinicalFindings(gearRequisition?.clinicalFindings ?? '');
				},
				isDirty
			}),
			[gearRequisition.clinicalFindings, gearRequisition.patientInstructions, isDirty, saveInstructions]
		);

		/**
		 * Fetches a list of patient instructions recently saved by the current order's pracitioner.
		 */
		const loadPreviousInstructions = ({ gsdlNo, orderableItemId }) => {
			if (gsdlNo && orderableItemId) {
				getPreviousPatientInstructions(gsdlNo, orderableItemId)
					.then((previousInstructions) => {
						setPreviousInstructions(previousInstructions);
					})
					.catch((error) => {
						// Ignore errors. No previous instructions will be displayed
					});
			}
		};

		const handlePatientInstructionsChange = (event) => {
			setPatientInstructions(event.target.value);
		};

		const handleClinicalFindingsChange = (event) => {
			setClinicalFindings(event.target.value);
		};

		const handleRouteNumberChange = (event) => {
			setRouteNumber(event.target.value);
		};

		return (
			<div>
				<div className="form-group">
					<label htmlFor="patient-instructions">
						Patient Instructions <small>(Will print on the requisition)</small>
					</label>
					{!!previousInstructions?.length && (
						<div>
							{previousInstructions.map((instruction, index) => {
								return (
									<div
										key={index}
										className="previous-patient-instructions"
										onClick={() => setPatientInstructions(instruction)}
									>
										{instruction}
									</div>
								);
							})}
						</div>
					)}
					<textarea
						id="patient-instructions"
						className="form-control"
						maxLength="512"
						rows="3"
						value={patientInstructions}
						onChange={handlePatientInstructionsChange}
					></textarea>
					<span className="pull-right">
						{patientInstructions.length}/{512} characters
					</span>
					<div className="clearfix"></div>
				</div>
				{showClinicalFindings && (
					<div className="form-group padding-top-lg">
						<label htmlFor="clinical-findings">
							Clinical Findings/Clinical Impressions <small>(Will print on the requisition)</small>
						</label>
						<textarea
							id="clinical-findings"
							className="form-control"
							maxLength="512"
							rows="3"
							value={clinicalFindings}
							onChange={handleClinicalFindingsChange}
						></textarea>
						<span className="pull-right">
							{clinicalFindings.length}/{512} characters
						</span>
						<div className="clearfix"></div>
					</div>
				)}

				<RouteNumberInput value={routeNumber} onChange={handleRouteNumberChange} ukOrder={isUKOrder(gearOrder)} />
				
				<div className="space-top">
					<TestPrepInfo />
				</div>
			</div>
		);
	}
);

export default DropShipInstructionsForm;
DropShipInstructionsForm.displayName = 'DropShipInstructionsForm';
